import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Header } from '@/components/layout/Header';
import { FCRAFooterText } from '@/components/layout/FCRADisclaimer';
import {
  CheckCircle,
  Sparkles,
  Database,
  Shield,
  ShieldCheck,
  BarChart,
  ArrowRight,
  Zap,
  TrendingDown,
  Award,
} from 'lucide-react';
import {
  WALLET_PRICING,
  ONE_TIME_PRICING,
  BATCH_PRICING,
  WALLET_TOPUP_OPTIONS,
  formatPrice,
  calculateWalletSavings,
} from '@/config/pricing';

export const PricingPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'wallet' | 'batch'>('wallet');

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Header />

      {/* Hero Section */}
      <section className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Simple, Transparent Pricing
            </h1>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-8">
              Professional skip tracing from $0.05 • AI scoring from $0.65 • No subscription required
            </p>
            <div className="flex flex-wrap justify-center gap-3 text-sm text-slate-600">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>Pay only for successful results</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>No monthly minimums</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>Free account to track history</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="pb-12 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="flex justify-center gap-2 bg-slate-100 p-1 rounded-lg max-w-md mx-auto">
            <button
              onClick={() => setActiveTab('wallet')}
              className={`
                flex-1 px-6 py-3 rounded-md font-semibold text-sm transition-all
                ${activeTab === 'wallet'
                  ? 'bg-white text-collectability-blue shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
                }
              `}
            >
              Web Search
            </button>
            <button
              onClick={() => setActiveTab('batch')}
              className={`
                flex-1 px-6 py-3 rounded-md font-semibold text-sm transition-all
                ${activeTab === 'batch'
                  ? 'bg-white text-collectability-blue shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
                }
              `}
            >
              Batch Upload
            </button>
          </div>
        </div>
      </section>

      {/* Wallet Pricing */}
      {activeTab === 'wallet' && (
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="pb-16 px-4"
        >
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-3">Web Search Pricing</h2>
              <p className="text-lg text-slate-600">
                Add funds to your wallet, deduct per search. Wallet never expires.
              </p>
            </div>

            {/* Pricing Comparison Table */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-12">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-slate-700">
                      Search Type
                    </th>
                    <th className="text-center py-4 px-6 text-sm font-semibold text-slate-700">
                      One-Time
                    </th>
                    <th className="text-center py-4 px-6 text-sm font-semibold text-slate-700 bg-collectability-blue-light">
                      With Wallet
                    </th>
                    <th className="text-center py-4 px-6 text-sm font-semibold text-green-700">
                      You Save
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {/* Enrich */}
                  <tr className="hover:bg-slate-50 transition-colors">
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <Database className="w-5 h-5 text-blue-500" />
                        <div>
                          <p className="font-semibold text-slate-900">Contact Enrichment</p>
                          <p className="text-xs text-slate-500">Phone, email, address, relatives</p>
                        </div>
                      </div>
                    </td>
                    <td className="text-center py-4 px-6 text-slate-600">
                      {formatPrice(ONE_TIME_PRICING.enrich)}
                    </td>
                    <td className="text-center py-4 px-6 bg-collectability-blue-light">
                      <span className="text-xl font-bold text-collectability-blue">
                        {formatPrice(WALLET_PRICING.enrich)}
                      </span>
                    </td>
                    <td className="text-center py-4 px-6">
                      <span className="text-green-700 font-semibold">
                        {calculateWalletSavings('enrich').savingsPercent}%
                      </span>
                    </td>
                  </tr>

                  {/* Validate */}
                  <tr className="hover:bg-slate-50 transition-colors">
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <Shield className="w-5 h-5 text-emerald-500" />
                        <div>
                          <p className="font-semibold text-slate-900">Identity Validation</p>
                          <p className="text-xs text-slate-500">Trust score, fraud risk, verification</p>
                        </div>
                      </div>
                    </td>
                    <td className="text-center py-4 px-6 text-slate-600">
                      {formatPrice(ONE_TIME_PRICING.validate)}
                    </td>
                    <td className="text-center py-4 px-6 bg-collectability-blue-light">
                      <span className="text-xl font-bold text-collectability-blue">
                        {formatPrice(WALLET_PRICING.validate)}
                      </span>
                    </td>
                    <td className="text-center py-4 px-6">
                      <span className="text-green-700 font-semibold">
                        {calculateWalletSavings('validate').savingsPercent}%
                      </span>
                    </td>
                  </tr>

                  {/* Enrich + Validate */}
                  <tr className="hover:bg-slate-50 transition-colors">
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <ShieldCheck className="w-5 h-5 text-collectability-blue" />
                        <div>
                          <p className="font-semibold text-slate-900">Complete Skip Trace</p>
                          <p className="text-xs text-slate-500">Enrichment + validation combined</p>
                        </div>
                      </div>
                    </td>
                    <td className="text-center py-4 px-6 text-slate-600">
                      {formatPrice(ONE_TIME_PRICING.enrich_validate)}
                    </td>
                    <td className="text-center py-4 px-6 bg-collectability-blue-light">
                      <span className="text-xl font-bold text-collectability-blue">
                        {formatPrice(WALLET_PRICING.enrich_validate)}
                      </span>
                    </td>
                    <td className="text-center py-4 px-6">
                      <span className="text-green-700 font-semibold">
                        {calculateWalletSavings('enrich_validate').savingsPercent}%
                      </span>
                    </td>
                  </tr>

                  {/* Scoring */}
                  <tr className="hover:bg-slate-50 transition-colors">
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <Sparkles className="w-5 h-5 text-purple-500" />
                        <div>
                          <p className="font-semibold text-slate-900">AI Collectability Scoring</p>
                          <p className="text-xs text-slate-500">Score, insights, recovery plans</p>
                        </div>
                      </div>
                    </td>
                    <td className="text-center py-4 px-6 text-slate-600">
                      {formatPrice(ONE_TIME_PRICING.scoring)}
                    </td>
                    <td className="text-center py-4 px-6 bg-collectability-blue-light">
                      <span className="text-xl font-bold text-collectability-blue">
                        {formatPrice(WALLET_PRICING.scoring)}
                      </span>
                    </td>
                    <td className="text-center py-4 px-6">
                      <span className="text-green-700 font-semibold">
                        {calculateWalletSavings('scoring').savingsPercent}%
                      </span>
                    </td>
                  </tr>

                  {/* Scoring + Enrich */}
                  <tr className="hover:bg-slate-50 transition-colors border-l-4 border-l-collectability-blue">
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <BarChart className="w-5 h-5 text-amber-500" />
                        <div>
                          <p className="font-semibold text-slate-900 flex items-center gap-2">
                            Full Intelligence Report
                            <Award className="w-4 h-4 text-amber-500" />
                          </p>
                          <p className="text-xs text-slate-500">Everything above + financial profile</p>
                        </div>
                      </div>
                    </td>
                    <td className="text-center py-4 px-6 text-slate-600">
                      {formatPrice(ONE_TIME_PRICING.scoring_enrich)}
                    </td>
                    <td className="text-center py-4 px-6 bg-collectability-blue-light">
                      <span className="text-xl font-bold text-collectability-blue">
                        {formatPrice(WALLET_PRICING.scoring_enrich)}
                      </span>
                    </td>
                    <td className="text-center py-4 px-6">
                      <span className="text-green-700 font-semibold">
                        {calculateWalletSavings('scoring_enrich').savingsPercent}%
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Wallet Top-Up Options */}
            <div className="bg-gradient-to-br from-collectability-blue to-blue-600 rounded-xl p-8 text-white mb-12">
              <h3 className="text-2xl font-bold mb-4">Wallet Top-Up Options</h3>
              <p className="text-blue-100 mb-6">
                Add funds to your wallet once, use for any search type. Get bonus credits on larger top-ups.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                {WALLET_TOPUP_OPTIONS.map((option) => (
                  <div
                    key={option.amount}
                    className={`
                      bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20
                      ${option.bonus > 0 ? 'ring-2 ring-white/40' : ''}
                    `}
                  >
                    <p className="text-2xl font-bold mb-1">{formatPrice(option.amount)}</p>
                    {option.bonus > 0 && (
                      <p className="text-sm text-green-300 font-semibold">
                        +{formatPrice(option.bonus)} bonus
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="text-center">
              <Link
                to="/search"
                className="inline-flex items-center gap-2 bg-collectability-blue text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-lg"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              >
                Start Searching <ArrowRight className="w-5 h-5" />
              </Link>
              <p className="text-sm text-slate-500 mt-4">
                Minimum $5 wallet top-up • No subscription required
              </p>
            </div>
          </div>
        </motion.section>
      )}

      {/* Batch Pricing */}
      {activeTab === 'batch' && (
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="pb-16 px-4"
        >
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-3">Batch Upload Pricing</h2>
              <p className="text-lg text-slate-600">
                Upload CSV files, pay per successful row. Volume discounts automatically applied.
              </p>
            </div>

            {/* 3-Tier Batch Pricing Table */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-12">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-slate-700">
                      Operation Type
                    </th>
                    <th className="text-center py-4 px-6 text-sm font-semibold text-slate-700">
                      5-24 rows
                    </th>
                    <th className="text-center py-4 px-6 text-sm font-semibold text-blue-700 bg-blue-50">
                      25-499 rows
                    </th>
                    <th className="text-center py-4 px-6 text-sm font-semibold text-green-700 bg-green-50">
                      500+ rows
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {/* Enrich */}
                  <tr className="hover:bg-slate-50 transition-colors">
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <Database className="w-5 h-5 text-blue-500" />
                        <span className="font-semibold text-slate-900">Enrich</span>
                      </div>
                    </td>
                    <td className="text-center py-4 px-6 text-slate-700 font-semibold">
                      {formatPrice(BATCH_PRICING.enrich.tier1)}/row
                    </td>
                    <td className="text-center py-4 px-6 bg-blue-50 text-blue-700 font-semibold">
                      {formatPrice(BATCH_PRICING.enrich.tier2)}/row
                    </td>
                    <td className="text-center py-4 px-6 bg-green-50 text-green-700 font-semibold">
                      {formatPrice(BATCH_PRICING.enrich.tier3)}/row
                    </td>
                  </tr>

                  {/* Validate */}
                  <tr className="hover:bg-slate-50 transition-colors">
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <Shield className="w-5 h-5 text-emerald-500" />
                        <span className="font-semibold text-slate-900">Validate</span>
                      </div>
                    </td>
                    <td className="text-center py-4 px-6 text-slate-700 font-semibold">
                      {formatPrice(BATCH_PRICING.validate.tier1)}/row
                    </td>
                    <td className="text-center py-4 px-6 bg-blue-50 text-blue-700 font-semibold">
                      {formatPrice(BATCH_PRICING.validate.tier2)}/row
                    </td>
                    <td className="text-center py-4 px-6 bg-green-50 text-green-700 font-semibold">
                      {formatPrice(BATCH_PRICING.validate.tier3)}/row
                    </td>
                  </tr>

                  {/* Skip Trace */}
                  <tr className="hover:bg-slate-50 transition-colors">
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <ShieldCheck className="w-5 h-5 text-collectability-blue" />
                        <span className="font-semibold text-slate-900">Complete Skip Trace</span>
                      </div>
                    </td>
                    <td className="text-center py-4 px-6 text-slate-700 font-semibold">
                      {formatPrice(BATCH_PRICING.enrich_validate.tier1)}/row
                    </td>
                    <td className="text-center py-4 px-6 bg-blue-50 text-blue-700 font-semibold">
                      {formatPrice(BATCH_PRICING.enrich_validate.tier2)}/row
                    </td>
                    <td className="text-center py-4 px-6 bg-green-50 text-green-700 font-semibold">
                      {formatPrice(BATCH_PRICING.enrich_validate.tier3)}/row
                    </td>
                  </tr>

                  {/* Scoring */}
                  <tr className="hover:bg-slate-50 transition-colors">
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <Sparkles className="w-5 h-5 text-purple-500" />
                        <span className="font-semibold text-slate-900">AI Scoring</span>
                      </div>
                    </td>
                    <td className="text-center py-4 px-6 text-slate-700 font-semibold">
                      {formatPrice(BATCH_PRICING.scoring.tier1)}/row
                    </td>
                    <td className="text-center py-4 px-6 bg-blue-50 text-blue-700 font-semibold">
                      {formatPrice(BATCH_PRICING.scoring.tier2)}/row
                    </td>
                    <td className="text-center py-4 px-6 bg-green-50 text-green-700 font-semibold">
                      {formatPrice(BATCH_PRICING.scoring.tier3)}/row
                    </td>
                  </tr>

                  {/* Full Intelligence */}
                  <tr className="hover:bg-slate-50 transition-colors border-l-4 border-l-collectability-blue">
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <BarChart className="w-5 h-5 text-amber-500" />
                        <span className="font-semibold text-slate-900 flex items-center gap-2">
                          Full Intelligence
                          <Award className="w-4 h-4 text-amber-500" />
                        </span>
                      </div>
                    </td>
                    <td className="text-center py-4 px-6 text-slate-700 font-semibold">
                      {formatPrice(BATCH_PRICING.scoring_enrich.tier1)}/row
                    </td>
                    <td className="text-center py-4 px-6 bg-blue-50 text-blue-700 font-semibold">
                      {formatPrice(BATCH_PRICING.scoring_enrich.tier2)}/row
                    </td>
                    <td className="text-center py-4 px-6 bg-green-50 text-green-700 font-semibold">
                      {formatPrice(BATCH_PRICING.scoring_enrich.tier3)}/row
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Batch Features */}
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <div className="bg-white rounded-lg p-6 shadow-md">
                <Zap className="w-10 h-10 text-collectability-blue mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  Automatic Volume Discounts
                </h3>
                <p className="text-sm text-slate-600">
                  No coupon codes needed. Discounts automatically applied based on your file size.
                </p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-md">
                <TrendingDown className="w-10 h-10 text-green-500 mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  Pay for Success Only
                </h3>
                <p className="text-sm text-slate-600">
                  Only charged for successfully processed rows. Invalid/error rows are free.
                </p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-md">
                <CheckCircle className="w-10 h-10 text-blue-500 mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  Results Stored 15 Months
                </h3>
                <p className="text-sm text-slate-600">
                  Re-download your results anytime within 15 months. No additional fees.
                </p>
              </div>
            </div>

            {/* CTA */}
            <div className="text-center">
              <Link
                to="/batch"
                className="inline-flex items-center gap-2 bg-collectability-blue text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-lg"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              >
                Upload CSV File <ArrowRight className="w-5 h-5" />
              </Link>
              <p className="text-sm text-slate-500 mt-4">
                Minimum 5 rows • CSV format • Pay only for successful results
              </p>
            </div>
          </div>
        </motion.section>
      )}

      {/* FAQ Section */}
      <section className="py-16 px-4 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-2">
                Do I need a subscription?
              </h3>
              <p className="text-slate-600">
                No. CollectABILITY is pay-as-you-go. Add funds to your wallet or process batches as needed. No monthly minimums or contracts.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-2">
                I have fewer than 5 records. What should I do?
              </h3>
              <p className="text-slate-600">
                Use Web Search for 1-4 lookups. It's faster and more cost-effective than batch processing for very small quantities.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-2">
                What if my batch has errors?
              </h3>
              <p className="text-slate-600">
                You only pay for successfully processed rows. Invalid or error rows are automatically excluded from billing.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-2">
                Do wallet funds expire?
              </h3>
              <p className="text-slate-600">
                No. Your wallet balance never expires. Use it whenever you need, at your own pace.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-2">
                How do volume discounts work?
              </h3>
              <p className="text-slate-600">
                Batch pricing automatically adjusts based on your file size. 5-24 rows get small batch pricing, 25-499 get standard, and 500+ get volume discounts—no codes needed.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-2">
                Can I re-download my results?
              </h3>
              <p className="text-slate-600">
                Yes. All results are stored for 15 months. You can re-download them anytime from your account dashboard at no additional cost.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <FCRAFooterText />
    </div>
  );
};
